/**
 * Opening Balance Entry — enter starting stock for multiple items at once.
 *
 * Dynamic list of item × warehouse × qty × rate entries. On submit calls
 * `trpc.stock.openingBalance` and navigates to /stock on success.
 *
 * Follows the warehouse-composer-page pattern:
 *   • Sticky composer-head with back link.
 *   • Section card containing the entry table.
 *   • Sticky composer-foot with submit button.
 *
 * @see packages/api-contract/src/procedures/stock.ts — openingBalanceInput
 */
import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { rupeesToPaise } from "../../../lib/format.ts";
import "./opening-balance-page.css";

interface EntryRow {
  id: number;
  itemId: string;
  warehouseId: string;
  quantity: string;
  rateRupees: string;
}

let nextId = 1;
function makeRow(): EntryRow {
  return { id: nextId++, itemId: "", warehouseId: "", quantity: "", rateRupees: "" };
}

export function OpeningBalancePage() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<EntryRow[]>([makeRow()]);
  const [errors, setErrors] = useState<string[]>([]);

  // Scope to goods only — services silently accept a `trackStock` flip in the
  // domain and would appear here with a phantom on-hand balance if picked.
  // (@see PR #145 review, finding #1)
  const itemsList = trpc.items.list.useQuery({ limit: 100, tab: "goods" }, { staleTime: 30_000 });
  const warehouseList = trpc.warehouses.list.useQuery(
    { includeInactive: false },
    { staleTime: 30_000 },
  );

  const utils = trpc.useUtils();
  const mutation = trpc.stock.openingBalance.useMutation({
    onSuccess: (data) => {
      // The domain flips `trackStock` on any non-tracked item so the opening
      // balance can be recorded. Surface that flip transparently — the user
      // asked for an opening balance, not stock-tracking, so this is a
      // "we handled it" note. (@see issue #137)
      const enabled = data.enabledTrackingItemIds.length;
      if (enabled > 0) {
        toast.success(
          `Enabled stock tracking on ${enabled} item${enabled === 1 ? "" : "s"}`,
          { description: "Opening balances recorded." },
        );
      }
      // Invalidate the stock listing so the just-created balances show up
      // immediately on /inventory/stock — otherwise TanStack Query serves stale cache
      // until a manual refresh. Fire-and-forget; never blocks navigation.
      // (@see issue #146)
      void utils.stock.invalidate();
      void navigate({ to: "/inventory/stock" });
    },
  });

  const updateEntry = (id: number, patch: Partial<EntryRow>) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };

  const removeEntry = (id: number) => {
    setEntries((prev) => (prev.length > 1 ? prev.filter((e) => e.id !== id) : prev));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errs: string[] = [];

    const validEntries = entries.filter((r) => r.itemId && r.warehouseId);
    if (validEntries.length === 0) {
      errs.push("Add at least one item before saving.");
    }

    const parsed = validEntries.map((r, i) => {
      const qty = parseInt(r.quantity, 10);
      if (!r.itemId) errs.push(`Row ${i + 1}: Item is required.`);
      if (!r.warehouseId) errs.push(`Row ${i + 1}: Warehouse is required.`);
      if (!qty || qty < 1) errs.push(`Row ${i + 1}: Quantity must be a positive integer.`);
      const ratePaise = rupeesToPaise(r.rateRupees);
      if (ratePaise === null || ratePaise < 1n)
        errs.push(`Row ${i + 1}: Rate must be a valid positive amount.`);
      return {
        itemId: r.itemId,
        warehouseId: r.warehouseId,
        quantity: qty,
        ratePaise: ratePaise ?? 0n,
      };
    });

    if (errs.length > 0) {
      setErrors(errs);
      return;
    }

    setErrors([]);
    mutation.mutate({ entries: parsed.map((p) => ({ ...p, ratePaise: p.ratePaise.toString() })) });
  };

  const items = (itemsList.data?.items ?? []) as Array<{
    id: string;
    name: string;
    sku: string | null;
    unit: string;
    trackStock: boolean;
  }>;
  // Every item is eligible in the picker — the domain auto-enables
  // `trackStock` on non-tracked items when the opening balance is recorded
  // (@see issue #137). Previously we filtered client-side, which produced a
  // silently empty dropdown for workspaces that hadn't turned on tracking yet.
  const warehouses = warehouseList.data?.warehouses ?? [];

  // Pre-empt the contract's dup-pair `refine`: don't offer a (item, warehouse)
  // combination that's already used in another row. Cheap O(rows) per lookup
  // — bounded by openingBalanceInput's .max(100).
  const isPairTaken = (rowId: number, itemId: string, warehouseId: string): boolean => {
    if (!itemId || !warehouseId) return false;
    return entries.some(
      (r) => r.id !== rowId && r.itemId === itemId && r.warehouseId === warehouseId,
    );
  };

  return (
    <div className="opening-balance-page">
      {/* composer-head */}
      <div className="composer-head">
        <div className="head-inner">
          <Link to="/inventory/stock" className="back">
            <ArrowLeft size={14} aria-hidden /> Stock summary
          </Link>
          <h1>Opening stock entry</h1>
          <span className="status-chip">New</span>
        </div>
      </div>

      <form className="composer-main" onSubmit={handleSubmit}>
        {errors.length > 0 && (
          <div className="error-banner" role="alert">
            <p>Please fix the following:</p>
            <ul>
              {errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        <section className="section">
          <div className="section-head">
            <span className="step">1</span>
            <div>
              <h3>Stock entries</h3>
              <span className="why">Enter your current stock levels — item, location, quantity, and cost rate.</span>
            </div>
          </div>
          <div className="section-body">
            {/* Column headers */}
            <div className="entry-header">
              <span>Item</span>
              <span>Warehouse</span>
              <span>Qty</span>
              <span>Rate (₹/unit)</span>
              <span aria-hidden />
            </div>

            {/* Entry rows */}
            <div className="entry-rows">
              {entries.map((row) => (
                <div key={row.id} className="entry-row">
                  {/* Item picker */}
                  <Select value={row.itemId} onValueChange={(v) => updateEntry(row.id, { itemId: v })}>
                    <SelectTrigger className="!h-9 !text-sm">
                      <SelectValue placeholder={items.length === 0 ? "No items yet — create one first" : "Select item…"} />
                    </SelectTrigger>
                    <SelectContent>
                      {items.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-muted">
                          No items yet.{" "}
                          <Link to="/sales/items/new" className="underline underline-offset-2 hover:text-ink">
                            Create one
                          </Link>{" "}
                          to record an opening balance.
                        </div>
                      ) : (
                        items.map((item) => (
                          <SelectItem
                            key={item.id}
                            value={item.id}
                            disabled={isPairTaken(row.id, item.id, row.warehouseId)}
                          >
                            {item.sku ? `${item.sku} — ` : ""}{item.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>

                  {/* Warehouse picker */}
                  <Select value={row.warehouseId} onValueChange={(v) => updateEntry(row.id, { warehouseId: v })}>
                    <SelectTrigger className="!h-9 !text-sm">
                      <SelectValue placeholder="Select warehouse…" />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses.map((w) => (
                        <SelectItem
                          key={w.id}
                          value={w.id}
                          disabled={isPairTaken(row.id, row.itemId, w.id)}
                        >
                          {w.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Quantity */}
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={row.quantity}
                    onChange={(e) => updateEntry(row.id, { quantity: e.target.value })}
                    placeholder="0"
                    className="right"
                    aria-label="Quantity"
                  />

                  {/* Rate */}
                  <div className="rate-wrap">
                    <span className="rate-prefix">₹</span>
                    <input
                      type="number"
                      min={0.01}
                      step={0.01}
                      value={row.rateRupees}
                      onChange={(e) => updateEntry(row.id, { rateRupees: e.target.value })}
                      placeholder="0.00"
                      className="right rate-input"
                      aria-label="Rate per unit in rupees"
                    />
                  </div>

                  {/* Remove row */}
                  <button
                    type="button"
                    onClick={() => removeEntry(row.id)}
                    disabled={entries.length === 1}
                    className="remove-btn"
                    aria-label="Remove row"
                  >
                    <Trash2 size={14} aria-hidden />
                  </button>
                </div>
              ))}
            </div>

            {/* Add row */}
            <button
              type="button"
              onClick={() => setEntries((prev) => [...prev, makeRow()])}
              className="add-row-btn"
            >
              <Plus size={12} aria-hidden />
              Add another item
            </button>
          </div>
        </section>

        {/* composer-foot */}
        <div className="composer-foot">
          <div className="foot-inner">
            <div className="foot-status">
              {entries.length} {entries.length === 1 ? "row" : "rows"} · confirm quantities and rates before saving.
            </div>
            <div className="foot-actions">
              <button
                type="submit"
                disabled={mutation.isPending}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-ink text-paper text-sm font-semibold hover:bg-ink-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {mutation.isPending ? "Saving…" : "Save opening stock"}
              </button>
              <button
                type="button"
                onClick={() => void navigate({ to: "/inventory/stock" })}
                className="inline-flex items-center h-9 px-3.5 rounded-md border border-line bg-paper text-sm font-semibold text-ink-2 hover:bg-bg"
              >
                Cancel
              </button>
              {mutation.error && (
                <span className="text-xs text-bad">{mutation.error.message}</span>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
